import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { isAxiosError } from 'axios';
import { toast } from 'vue3-toastify';

import registerApi from '~/services/register-api/register-api';
import { queryKeys } from '~/store/queryClient';

/* eslint-disable perfectionist/sort-modules -- helpers antes dos hooks */

const UPDATE_COMPANY_ERRORS: Record<number, string> = {
  401: 'Token inválido ou ausente.',
  404: 'Empresa não encontrada.',
  412: 'Empresa sem parceiro vinculado.',
  422: 'Erro de validação nos dados enviados.',
};

const ESCROW_GENERIC_ERROR = 'Não foi possível reenviar a abertura de conta. Tente novamente mais tarde.';

const ESCROW_DETAIL_MESSAGES: Record<string, string> = {
  'company must be in create_escrow_account status': 'A empresa não está no status correto para esta operação.',
  'company not found': 'Empresa não encontrada.',
  'escrow account opening already finished': 'A abertura de conta escrow já foi finalizada.',
  'escrow account opening still in progress, wait before restarting': (
    'Abertura de conta em andamento. Aguarde antes de reiniciar.'
  ),
  'Unauthorized - Invalid or expired token': 'Token inválido ou ausente.',
};

const MUTATION_MAX_RETRIES = 3;
const NO_RETRY_STATUS = new Set([401, 403, 404, 409, 412, 422]);

function getHttpErrorMessage(
  error: unknown,
  messages: Record<number, string>,
  fallback: string,
): string {
  if (isAxiosError(error) && error.response?.status) {
    return messages[error.response.status] ?? fallback;
  }
  return fallback;
}

function mutationRetry(failureCount: number, error: unknown): boolean {
  if (isAxiosError(error) && error.response && NO_RETRY_STATUS.has(error.response.status)) {
    return false;
  }
  return failureCount < MUTATION_MAX_RETRIES;
}

function parseEscrowErrorMessage(error: unknown): string {
  if (!isAxiosError(error) || !error.response) {
    return ESCROW_GENERIC_ERROR;
  }

  const detail = error.response.data?.detail;
  if (typeof detail === 'string' && ESCROW_DETAIL_MESSAGES[detail]) {
    return ESCROW_DETAIL_MESSAGES[detail];
  }

  return ESCROW_GENERIC_ERROR;
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      officialName,
    }: {
      companyId: number;
      officialName: string;
      partnerId: number;
    }) => (
      registerApi.company.update(companyId, { official_name: officialName })
    ),
    onError: (error: unknown) => {
      toast.error(getHttpErrorMessage(error, UPDATE_COMPANY_ERRORS, 'Erro ao atualizar razão social'));
    },
    onSuccess: async (_data, { partnerId }) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.REGISTER_PARTNER.DETAILS(partnerId),
      });
      toast.success('Razão social atualizada com sucesso!');
    },
  });
}

export function useStartEscrowAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyId: number) => registerApi.company.startEscrowAccount(companyId),
    onError: (error: unknown) => {
      toast.error(parseEscrowErrorMessage(error));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.REGISTER_PARTNER.DETAILS_ALL });
      toast.success('Solicitação de abertura de conta reenviada com sucesso!');
    },
    retry: mutationRetry,
    retryDelay: (attempt) => attempt * 1000,
  });
}

/* eslint-enable perfectionist/sort-modules */
